import {
  POST_PUBLISH_SCHEMA_VERSION,
  type PostPublishJob,
  type PostPublishQueueEnvelope,
  type PostPublishStatus,
  type SerializedPostPublishError,
} from "./postPublishTypes";
import { firebaseRecoveryError } from "./realtimeRecovery";

function redactSensitiveText(value: string) {
  return value
    .replace(/([?&]token=)[^&\s]+/gi, "$1[redacted]")
    .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, "Bearer [redacted]");
}

export class PoolCanceledError extends Error {
  code = "post/canceled";
  constructor() {
    super("Publication annulee.");
    this.name = "PoolCanceledError";
  }
}

export async function mapWithConcurrency<T, R>(params: {
  items: readonly T[];
  concurrency: number;
  worker: (item: T, index: number) => Promise<R>;
  signal?: AbortSignal;
}): Promise<R[]> {
  const { items, worker, signal } = params;
  const limit = Math.max(1, Math.floor(params.concurrency));
  const results = new Array<R>(items.length);
  let cursor = 0;
  let workerFailure: unknown;

  const run = async () => {
    while (true) {
      if (signal?.aborted) throw new PoolCanceledError();
      if (workerFailure) return;
      const index = cursor;
      cursor += 1;
      if (index >= items.length) return;
      try {
        results[index] = await worker(items[index], index);
      } catch (error) {
        workerFailure = error;
        throw error;
      }
    }
  };

  const settlements = await Promise.allSettled(
    Array.from({ length: Math.min(limit, items.length) }, run)
  );
  const failure = settlements.find(
    (settlement): settlement is PromiseRejectedResult => settlement.status === "rejected"
  );
  if (failure) throw failure.reason;
  return results;
}

export function aggregatePostProgress(job: Pick<PostPublishJob, "attachments">) {
  const total = job.attachments.length;
  const completed = job.attachments.filter((item) => item.status === "uploaded").length;
  return { completed, total, fraction: total === 0 ? 1 : completed / total };
}

const VALID_STATUSES = new Set<PostPublishStatus>([
  "preparing", "queued", "uploading", "committing", "awaiting-moderation", "published", "failed", "canceled",
]);

function isJob(value: unknown): value is PostPublishJob {
  if (!value || typeof value !== "object") return false;
  const job = value as Partial<PostPublishJob>;
  return job.schemaVersion === POST_PUBLISH_SCHEMA_VERSION
    && typeof job.jobId === "string"
    && typeof job.postId === "string"
    && typeof (job.authorUid ?? job.ownerUid) === "string"
    && typeof job.createdAtLocal === "number"
    && typeof job.updatedAtLocal === "number"
    && typeof job.status === "string"
    && VALID_STATUSES.has(job.status as PostPublishStatus)
    && Array.isArray(job.attachments)
    && !!job.author
    && typeof job.author === "object"
    && job.author.authorUid === (job.authorUid ?? job.ownerUid);
}

export function serializePostPublishJobs(jobs: PostPublishJob[]) {
  const envelope: PostPublishQueueEnvelope = {
    schemaVersion: POST_PUBLISH_SCHEMA_VERSION,
    jobs: jobs.map((job) => ({
      ...job,
      attachments: job.attachments.map((attachment) => ({
        ...attachment,
        storage: attachment.storage ? { ...attachment.storage, url: undefined } : undefined,
        error: attachment.error ? { ...attachment.error, message: redactSensitiveText(attachment.error.message) } : undefined,
      })),
      error: job.error ? { ...job.error, message: redactSensitiveText(job.error.message) } : undefined,
    })),
  };
  return JSON.stringify(envelope);
}

export function deserializePostPublishJobs(raw: string | null): PostPublishJob[] {
  if (!raw) return [];
  try {
    const value = JSON.parse(raw) as Partial<PostPublishQueueEnvelope>;
    if (value.schemaVersion !== POST_PUBLISH_SCHEMA_VERSION || !Array.isArray(value.jobs)) {
      return [];
    }
    return value.jobs.filter(isJob).map((job) => {
      const attachments = job.attachments.map((attachment) => attachment.status === "uploading"
        ? { ...attachment, status: "ready" as const }
        : attachment);
      return {
        ...job,
        authorUid: job.authorUid ?? job.ownerUid!,
        ownerUid: undefined,
        attachments,
        status: job.status === "uploading" || job.status === "committing"
          ? "queued"
          // Jobs written by the previous client were already acknowledged by
          // Firestore, but their moderation result was not yet visible.
          : job.status === "published"
            ? "awaiting-moderation"
            : job.status,
        progress: aggregatePostProgress({ attachments }),
      };
    });
  } catch {
    return [];
  }
}

export function collectPersistedPostPublishJobIds(
  entries: ReadonlyArray<readonly [string, string | null]>,
  keys: { queueKeyPrefix: string; legacyQueueKey: string; composerDraftKeyPrefix: string }
) {
  const jobIds = new Set<string>();
  for (const [key, raw] of entries) {
    if (!raw) continue;
    if (key === keys.legacyQueueKey || key.startsWith(keys.queueKeyPrefix)) {
      deserializePostPublishJobs(raw).forEach((job) => jobIds.add(job.jobId));
      continue;
    }
    if (!key.startsWith(keys.composerDraftKeyPrefix)) continue;
    try {
      const value: unknown = JSON.parse(raw);
      if (value && typeof value === "object" && typeof (value as { jobId?: unknown }).jobId === "string") {
        jobIds.add((value as { jobId: string }).jobId);
      }
    } catch {
      // An invalid draft cannot be restored, so it does not protect a file directory.
    }
  }
  return jobIds;
}

export function serializePublishError(
  error: unknown,
  stage: string
): SerializedPostPublishError {
  const value = error as { code?: unknown; message?: unknown } | null;
  const recovery = firebaseRecoveryError(error);
  const code = recovery.code;
  const storageRetryable =
    code === "storage/timeout" ||
    code === "storage/unknown" ||
    code === "storage/retry-limit-exceeded";
  return {
    code,
    message: redactSensitiveText(
      typeof value?.message === "string" ? value.message : "La publication a echoue."
    ),
    retryable: recovery.retryable || storageRetryable,
    stage,
    httpStatus: recovery.status,
  };
}

export function isRetryablePostPublishError(error: unknown) {
  return serializePublishError(error, "unknown").retryable;
}

export function preparePostPublishRetry(job: PostPublishJob, now = Date.now()) {
  return {
    ...job,
    status: "queued" as const,
    error: undefined,
    attachments: job.attachments.map((attachment) =>
      attachment.status === "failed"
        ? { ...attachment, status: "ready" as const, error: undefined }
        : attachment
    ),
    updatedAtLocal: now,
  };
}

export function mergeRemotePostsWithJobs<T extends { id: string }>(
  remote: readonly T[],
  jobs: readonly PostPublishJob[]
) {
  const remoteIds = new Set(remote.map((post) => post.id));
  return {
    remote: [...remote],
    local: jobs.filter(
      (job) => job.status !== "canceled" && !remoteIds.has(job.postId)
    ),
    reconciledJobIds: jobs.filter((job) => remoteIds.has(job.postId)).map((job) => job.jobId),
  };
}

/** Strict UI/storage partition; the caller must provide the authenticated UID. */
export function jobsForPostPublishAuthor(jobs: readonly PostPublishJob[], uid: string | null | undefined) {
  return uid ? jobs.filter((job) => job.authorUid === uid) : [];
}

/** A worker is never allowed to act on a job from another authenticated account. */
export function canRunPostPublishJob(job: Pick<PostPublishJob, "authorUid">, uid: string | null | undefined) {
  return !!uid && uid === job.authorUid;
}

export function shouldNormalizeImage(input: {
  mimeType?: string;
  width?: number;
  height?: number;
  size?: number;
}) {
  // Always canonicalize picker output once: mime sniffing, EXIF orientation and
  // encoder behavior cannot be guaranteed from picker metadata alone.
  void input;
  return true;
}

export function canBuildDownloadUrlFromUploadResponse() {
  // The REST upload metadata does not contractually guarantee a Firebase
  // download token. Keep the SDK lookup instead of constructing a fragile URL.
  return false;
}

export function isOwnedPostPublishPath(path: string, uid: string, postId: string) {
  const prefixes = [
    `post_images/${uid}/${postId}/`,
    `moderation-pending/${uid}/${postId}/`,
  ];
  return prefixes.some((prefix) =>
    path.startsWith(prefix)
      && path.length > prefix.length
      && !path.slice(prefix.length).includes("/")
      && path.endsWith(".jpg")
  );
}

export function isIdempotentCommitConflict(error: { code?: string; status?: number }) {
  return error.code === "already-exists" || error.status === 409;
}

export function shouldReconcileCommitError(error: { code?: string; status?: number }) {
  return isIdempotentCommitConflict(error)
    || error.code === "unavailable"
    || error.code === "deadline-exceeded";
}

export async function withSingleTokenRefresh<T>(params: {
  getToken: (forceRefresh: boolean) => Promise<string>;
  operation: (token: string) => Promise<T>;
  isUnauthorized?: (error: unknown) => boolean;
}) {
  const isUnauthorized = params.isUnauthorized ?? ((error: unknown) => {
    const code = (error as { code?: string }).code;
    return code === "unauthenticated" || code === "storage/unauthenticated";
  });
  let token = await params.getToken(false);
  try {
    return await params.operation(token);
  } catch (error) {
    if (!isUnauthorized(error)) throw error;
    token = await params.getToken(true);
    return params.operation(token);
  }
}

export function isOwnedDurableJobUri(uri: string, jobId: string) {
  const normalized = uri.replace(/\\/g, "/");
  const marker = `/post-publish/${jobId}/`;
  return normalized.includes(marker) && !normalized.slice(normalized.indexOf(marker) + marker.length).includes("/");
}

export async function cleanupOwnedDurableJobFiles(params: {
  jobId: string;
  uris: string[];
  remove: (uri: string) => Promise<void>;
}) {
  const owned = Array.from(new Set(params.uris.filter((uri) => isOwnedDurableJobUri(uri, params.jobId))));
  await Promise.all(owned.map(params.remove));
}
